import React, {
  RefObject,
  KeyboardEvent,
  Dispatch,
  HTMLInputTypeAttribute,
  SetStateAction,
  ReactNode,
  Key,
} from "react";
import { StateSetter, StringOrUndefined } from "./GlobalModels.model";
import { SearchItemType } from "./Search.model";
import type { MimeType } from "./Products.model";

export interface ITextInputProps {
  label: string;
  placeholder: string;
  error: StringOrUndefined;
  onChange: (text: string) => void;
  value: string;
  disabled?: boolean;
  customClassName?: string;
  type?: HTMLInputTypeAttribute;
  onBlur?: () => void;
}
export type CodeChangePayload = {
  data: string[];
  isComplete: boolean;
};

type HTMLInputType = Extract<HTMLInputTypeAttribute, "text" | "number">;

export type IVerificationInputProps = {
  length: number;
  onChange: Dispatch<CodeChangePayload>;
  error: StringOrUndefined;
  customErrorStyle?: string;
  customInputStyle?: string;
  customContainerStyle?: string;
  type?: HTMLInputType;
};

export type TextInputRefMapping = {
  [key: number]: HTMLInputElement | null;
};
export type IVerificationInputUpdateProps = {
  inputsRefs: RefObject<TextInputRefMapping>;
  length: number;
  type?: "text" | "number";
};

export interface IVerificationInputUpdateReturn {
  code: string[];
  onKeyPress: (event: KeyboardEvent<HTMLInputElement>, index: number) => void;
  handleChange: (value: string, index: number) => void;
  warning: StringOrUndefined;
  handlePasteEvent: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

export enum ValidationRuleType {
  LENGTH = "length",
  DIGITS = "digits",
  SPECIAL_CHARS = "specialChars",
  UPPERCASE = "uppercase",
  LOWERCASE = "lowercase",
}

export interface IValidationRule {
  type: ValidationRuleType;
  requiredOccurrences: number;
  message?: string;
}

export interface IPasswordInputProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (data: string) => void;
  error: StringOrUndefined;
  rules?: IValidationRule[];
  customContainerStyle?: string;
  customErrorStyle?: string;
  customInputStyle?: string;
}
export interface IUsePasswordValidationArgs {
  value: StringOrUndefined;
  rules: IValidationRule[];
}

export interface IPasswordValidation {
  message: string;
  isValid: boolean;
}
export interface IUsePasswordValidationReturn {
  results: IPasswordValidation[];
}

export interface IAlternativeAuthProps {
  message: string;
  handleGoogleAction: () => void;
  handleAppleAction: () => void;
  showGoogle?: boolean;
  showApple?: boolean;
  className?: string;
}
export interface ISubmissionProps {
  buttonText: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
}


export interface ICheckBoxProps {
  value: boolean;
  onChange: StateSetter<boolean>;
  message: string;
  errorMessage?: StringOrUndefined;
  customStyle?: string;
  messageAction?: () => void;
  disableCheckBox: boolean;
}

export interface ITimeCountDownProps {
  message: string;
  time: number;
  action: (callback: Dispatch<SetStateAction<boolean>>) => void;
}

export interface IRadioButtonOption {
  label: string;
  value: string;
}

export interface IRadioButtonGroupProps {
  label: string;
  options: IRadioButtonOption[];
  defaultValue?: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  customClassName?: string;
}

export interface IProductDetailsPlaceholderProps {
  showGallery?: boolean;
  showThumbnails?: boolean;
  showDescription?: boolean;
  showTechDetails?: boolean;
  className?: string;
  thumbnailCount?: number;
  showTitle?: boolean;
}

export interface ICardPlaceholderProps {
  count?: number;
  customWrapperStyle?: string;
}

export interface IUseQuantityControlReturn {
  inputValue: string;
  error: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputBlur: () => void;
  handleIncrease: () => void;
  handleDecrease: () => void;
  disableIncrement: boolean;
  disableDecrement: boolean;
}

export interface IAddToCartControlProps {
  quantity: number;
  onQuantityChange: (val: number) => void;
  onClick: (quantity: number) => void | Promise<void>;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  loading?: boolean;
  buttonText?: string;
  infoText?: string;
  error?: string;
  customWrapperClass?: string;
  quantityControlClass?: string;
  buttonClass?: string;
  infoTextClass?: string;
  errorTextClass?: string;
  onError?: (error: string) => void;
}

export interface ISearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  typesFilter?: SearchItemType[];
  emptyEnterPath?: string;
  enableDropdown?: boolean;
}

export interface CarouselProps extends React.PropsWithChildren {
  step?: number;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
  className?: string;
  navClassName?: string;
  prevBtnClassName?: string;
  nextBtnClassName?: string;
  viewportClassName?: string;
  trackClassName?: string;
  slideClassName?: string;
  wrapSlides?: boolean;
  prevIcon?: ReactNode;
  nextIcon?: ReactNode;
  resetKey?: Key;
}

export type ExistingUploadFile = {
  id: string;
  name: string;
  url?: string;
  type?: string;
};

export type UploadThumbnailType = "pdf" | "image" | "fallback";

export type UploadRowViewModel = {
  key: string;
  kind: "existing" | "new";
  fileName: string;
  metaText: string;
  thumbnailType: UploadThumbnailType;
  thumbnailUrl: string | null;
  canRemove: boolean;
  onRemove?: () => void;
  testId: string;
  url?: string | null;
  labelValue?: string;
  labelError?: string;
  onLabelChange?: (value: string) => void;
  onLabelBlur?: () => void;
};

export type UploadViewModel = {
  inputRef: React.RefObject<HTMLInputElement | null>;
  accept: string;
  allowMultiple: boolean;
  hasFiles: boolean;
  rows: UploadRowViewModel[];
  primaryButtonLabel: string;
  primaryButtonDisabled: boolean;
  primaryButtonTitle?: string;
  remainingMessage: string | null;
  helperMessage?: string;
  errorMessage: string | null;
  openFilePicker: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export interface LabeledFile {
  file: File;
  fileLabel: string;
}

export interface UploadProps {
  value?: LabeledFile[] | FileList | null;
  onChange: (value: LabeledFile[] | FileList | null) => void;
  mimeTypes: MimeType[];
  required?: boolean;
  maxSizeMB?: number;
  maxFiles?: number;
  maxTotalMB?: number;
  badTypeMessage?: string;
  error?: string | null;
  helperText?: string;
  showInlineError?: boolean;
  existingFiles?: ExistingUploadFile[];
  onRemoveExisting?: (index: number) => void;
  requiresLabel?: boolean;
  parentTestId: string;
}



export type DropdownMediaOption = {
  value: string;
  label: string;
  description?: string | null;
  imageUrl?: string | null;
};
export interface DropdownMedia {
  value: string;
  onChange: (value: string) => void;
  options: DropdownMediaOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: string | null;
  buildImageSrc?: (url?: string | null) => string | null;
  showThumbnail?: boolean;
}

export interface IQuantityControlProps {
  value: number;
  onChange: (quantity: number) => void;
  onPreviewChange?: (quantity: number) => void;
  min?: number;
  max?: number;
  step?: number;
  customContainerClass?: string;
  inputClass?: string;
  incrementButtonClass?: string;
  decrementButtonClass?: string;
  disabled?: boolean;
  onError?: (msg: string | null) => void;
}

export interface IUseQuantityControlProps {
  value: number;
  onChange: (quantity: number) => void;
  onPreviewChange?: (quantity: number) => void;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onError?: (msg: string | null) => void;
}
